import React, { createContext, useContext, useEffect, useState } from 'react';
import { THEMES } from '../constants';
import { Theme } from '../types';

type ThemeContextType = {
  /**
   * Theme value for the MLP
   */
  theme: Theme;
  /**
   * state hook to set Theme context for the MLP
   */
  setPageTheme: (_theme: Theme) => void;
};

/**
 * React Context for managing Theme for MLP
 */
const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

/**
 * React Context Provider for managing Theme context for MLP
 */
export const ThemeProvider = ({ children }: { children: React.ReactNode }) => {
  const [theme, setTheme] = useState<Theme>(THEMES.BASE_LIGHT);

  const setPageTheme = (theme: Theme) => {
    if (Object.values(THEMES).includes(theme)) {
      setTheme(theme);
      document.body.setAttribute('data-theme', theme);
    }
  };

  // Set data-theme attribute on body, to apply styles to complete page
  // data-theme is used like body[data-them] in styles/theme.css file to set global theme styles
  useEffect(() => {
    document.body.setAttribute('data-theme', theme);
  }, [theme]);

  return <ThemeContext.Provider value={{ theme, setPageTheme }}>{children}</ThemeContext.Provider>;
};

/**
 * Provides Theme Context
 */
export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
};
